import { defineStore } from "pinia";
import { ref, reactive, computed } from "vue";
import { type Link } from "@interfaces";
import { useStorage } from "@vueuse/core";
interface Link {
    url: string;
    text: string;
    position: string;
    social?: boolean;
    profile?: "Instagram" | "Facebook" | "X" | "Pinterest" | "YouTube" | "TikTok";
    sort?: number;
    secondary?: Link[];
    tertiary?: Link[];
    sales: boolean;
}

function sortLinks(a: Link, b: Link) {
    if (a.sort && b.sort) {
        return a.sort - b.sort;
    }
    if (a.sort) {
        return -1;
    }
    if (b.sort) {
        return 1;
    }
    return 0;
}

export const useSettingsStore = defineStore("settings", {
    state: () => {
        const links: Link[] = [];
        const list: List = {
            count: 0
        };
        const categoryLinks: Link[] = [];

        return {
            categories: useStorage("categories", []),
            overlayMessage: null,
            links,
            list,
            total_after_discount: null,
            categoryLinks,
            is_dealer: false,
            dealerNo: "",
            username: "",
            email: "",
            accountLevel: "",
            isLoginFlyoutOpened: false,
            isHeaderFlyoutOpened: false,
            isBuystackFlyoutOpened: false,
            quickView: {
                product_seo_name: "",
                color: "",
                rothco_item_no: "",
                item_index: null,
                product: {
                    seo_name: "",
                    images: [],
                    item_variations: [],
                    item_specifications: [],
                    prices: []
                },
                prices: [],
                disable_quantity_input: false,
                item_list_info: {list_id: null, list_item_id: null, quantity: 1},
                coupon_info: {},
                open_details_to_new_tab: false
            },
            isLoadingQuickView: false,
            quickViewErrorMessage: "",
            buystackFlyoutListItemUpdate: null,
            user_id: null,
            dealer_no: null,
            freeShippingAmount: useStorage("freeShippingAmount",null),
            freeShippingByCoupon: false,
            allow_restricted_items: false,
            flyoutMessage: null,
            notificationMessage: null,
            notificationTimeout: null
        };
    },
    getters: {
        isApiServer(state) {
            return import.meta.env.VITE_API_PORTAL === 'true';
            // TODO: Replace this with actual check for env variable
        },
        freeShippingMessage() {
            if (this.freeShippingProgressPercentage < 100 && !this.freeShippingByCoupon) {
                return `You are $${(this.freeShippingAmount - this.totalAfterDiscount).toFixed(2)} away from free shipping!`;
            }
            return "You qualify for free ground shipping!";
        },
        freeShippingProgressPercentage(state) {
            const progress =
                state.freeShippingAmount > 0
                    ? state.totalAfterDiscount / state.freeShippingAmount
                    : 1;
            return progress > 1 ? 100 : progress * 100;
        },
        user() {
            return {
                username: this.username,
                email: this.email,
                user_id: this.user_id,
                is_dealer: this.is_dealer,
                dealer_no: this.dealer_no
            };
        },
        currentList(state) {
            return state.list;
        },
        totalAfterDiscount(state) {
            return state.total_after_discount > 0 ? state.total_after_discount : state.list.total;
        },
        accountDisplay(state, getters) {
            if (state.username == "" || state.username == null) {
                return { text: "Account", subText: "Login" };
            }
            if (state.is_dealer) {
                return { text: "Account", subText: "Manage" };
            }
            return { text: "My Account", subText: "View" };
        },
        isLoggedIn(state) {
            return state.user_id != null;
        },
        userType(state) {
            return !state.is_dealer ? "b2c" : "b2b";
        },
        userInfo(state) {
            return {
                id: state.user_id,
                username: state.username,
                email: state.email,
                isDealer: state.is_dealer,
                dealerNo: state.dealerNo,
                role: state.accountLevel
            };
        },
        headerLinks(state): Link[] {
            const linkMap = new Map();
            for (const link of state.links) {
                if(!linkMap.has(link.text)){
                    linkMap.set(link.text, link);
                }
            }
            for (const categoryLink of state.categoryLinks) {
                if (!linkMap.has(categoryLink.text)) {
                    linkMap.set(categoryLink.text, categoryLink);
                } else {
                    let existingLink = JSON.parse(JSON.stringify(linkMap.get(categoryLink.text)));

                    if (existingLink.secondary && categoryLink.secondary) {
                        existingLink.secondary = existingLink.secondary.concat(
                            categoryLink.secondary
                        );
                    }else{
                        existingLink.secondary = categoryLink.secondary;
                    }
                    existingLink.url = categoryLink.url;
                    existingLink.urlParams = categoryLink.urlParams;
                    existingLink.shopAllLink = categoryLink.shopAllLink;
                    existingLink.palleteLinks = categoryLink.palleteLinks ?? {};
                    linkMap.set(categoryLink.text, existingLink);
                }
            }
            const combinedLinks: Link[] = Array.from(linkMap.values());
            return combinedLinks
                .filter(
                    (l: Link) => l.position == "header" || l.position == null
                )
                .sort((a, b) => {
                    // if link has a sort of null, sort as if it were 1
                    if (a.sort == null) {
                        a.sort = 1;
                    }
                    if (b.sort == null) {
                        b.sort = 1;
                    }
                    return a.sort - b.sort;
                });
        },
        allFooterLinks(state): Link[] {
            return state.links
            .filter((l: Link) => l.position == "footer");
        },
        footerLinks(state): Link[] {
            return state.links
            .filter((l: Link) => l.position == "footer").filter(link => !link.social).sort((a, b) => sortLinks(a, b));
        },
        socialLinks(state): Link[] {
            return state.links
            .filter((l: Link) => l.position == "footer").filter(link => link.social).sort((a, b) => sortLinks(a, b));
        },
        canSeePricing(state): Boolean {
            if(!state.is_dealer){
                return true;
            }
            return ["See Pricing", "Place Orders", "Administrator"].includes(
                state.accountLevel
            );
        },
        canPlaceOrders(state): Boolean {
            if(!state.is_dealer){
                return true;
            }
            return ["Place Orders", "Administrator"].includes(
                state.accountLevel
            );
        },
        canHandleAdmin(state): Boolean {
            if(!state.is_dealer){
                return true;
            }
            return ["Administrator"].includes(
                state.accountLevel
            );
        }
    },
    actions: {
        calculatePromotionalShippingProgress(cost: number) {
            let progress = 1 - ((this.totalAfterDiscount) / cost);
            return progress > 1 ? 100 : 100 - Math.round(progress * 100);
        },
        addLink(link: Link) {
            this.links.push(link);
        },
        removeLink(link: Link) {
            const index = this.links.findIndex((l: Link) => l.url == link.url);
            this.links.splice(index, 1);
        },
        setList(list: List) {
            this.list = list;
        },
        appendLinks(links: Link[]) {
            for (let link of links) {
                let hasCategory = this.categoryLinks.some(
                    (l: Link) => l.text == link.text
                );
                if (!hasCategory) {
                    this.categoryLinks.push(link);
                }
            }
        },
        setListTotalAfterDiscount(total) {
            this.total_after_discount = total;
        },
        setUser(user) {
            if (user?.user_id != null) {
                this.is_dealer = user.is_dealer;
                this.username = user.username;
                this.email = user.email;
                this.user_id = user.user_id;
                this.dealer_no = user.dealer_no;
                if (this.user_id) {
                    this.setShopperId(this.user_id);
                }
                this.dealerNo = user.dealer_no;
                this.accountLevel = user.account_level_name;
                this.allow_restricted_items = user.allow_restricted_items == 1;
                if(typeof window !== 'undefined'){
                    if(window.sentry){
                        let replay = window.sentry.getReplay();
                        replay.start();
                        window.sentry.setUser({
                            id: this.user_id,
                            email: this.email,
                            username: this.username,
                            rothco_dealer_no: this.dealerNo,
                        })
                    }
                }
            } else {
                this.is_dealer = false;
                this.username = "";
                this.email = "";
                this.user_id = null;
                this.dealer_no = null;
                this.dealerNo = "";
                this.accountLevel = "";
                if(typeof window !== 'undefined'){
                    if(window.sentry){
                        let replay = window.sentry.getReplay();
                        replay.stop();
                        window.sentry.setUser({
                            id: null,
                            email: null,
                            username: null,
                            rothco_dealer_no: null,
                        })
                    }
                }
            }
        },
        toggleFlyout(flyoutMessage = null) {
            if (!this.isLoggedIn) {
                this.isLoginFlyoutOpened = !this.isLoginFlyoutOpened;
                this.isHeaderFlyoutOpened = false;
                if(flyoutMessage){
                    this.flyoutMessage = flyoutMessage;
                }else{
                    this.flyoutMessage = null;
                }
            } else {
                this.isLoginFlyoutOpened = false;
                this.isHeaderFlyoutOpened = !this.isHeaderFlyoutOpened;
                this.flyoutMessage = null;
            }
        },
        closeLoginFlyout() {
            this.isLoginFlyoutOpened = false;
        },
        setShopperId(shopperId) {
            try {
                if(typeof IntelliSuggest !== 'undefined') {
                    IntelliSuggest.setShopperId(shopperId);
                }
            } catch (e) {
                if(!['production', 'beta'].includes(import.meta.env.VITE_APP_ENV)){
                    console.error("Intellisuggest set shopper ID error: ", e);
                }
            }
        },
        setFreeShippingAmount(amount) {
            this.freeShippingAmount = amount;
        },
        setFreeShippingByCoupon(freeShippingApplied) {
            this.freeShippingByCoupon = freeShippingApplied;
        },
        setOverlayMessage(message) {
            this.overlayMessage = message;
        },

        toggleBuystackFlyout({
            seo_name, color, 
            rothco_item_no,
            item_index,
            item_list_info, 
            coupon_info,
            disable_quantity_input, 
            onListItemUpdate = null,
            open_details_to_new_tab = false
        }) {
            this.isLoginFlyoutOpened = false;
            this.isHeaderFlyoutOpened = false;
            this.quickViewErrorMessage = "";
            this.isBuystackFlyoutOpened = !!seo_name;
            if(seo_name){
                this.quickView.product_seo_name = seo_name;
            }

            if(color){
                this.quickView.color = color;
            }else{
                this.quickView.color = null;
            }

            if(rothco_item_no){
                this.quickView.rothco_item_no = rothco_item_no;
            }else{
                this.quickView.rothco_item_no = null;
            }

            if(item_index != null){
                this.quickView.item_index = item_index;
            }else{
                this.quickView.item_index = null;
            }

            if(disable_quantity_input){
                this.quickView.disable_quantity_input = disable_quantity_input;
            }else{
                this.quickView.disable_quantity_input = false;
            }

            if(item_list_info){
                this.quickView.item_list_info = item_list_info;
            }else{
                this.quickView.item_list_info = {list_id: null, list_item_id: null, quantity: 1};
            }

            if(coupon_info){
                this.quickView.coupon_info = coupon_info;
            }else{
                this.quickView.coupon_info = {};
            }

            if(open_details_to_new_tab){
                this.quickView.open_details_to_new_tab = open_details_to_new_tab;
            }else{
                this.quickView.open_details_to_new_tab = false;
            }

            this.buystackFlyoutListItemUpdate = seo_name ? onListItemUpdate : null;
        },
        setQuickViewProduct(product, prices){
            this.quickView = {
                product_seo_name: product?.seo_name ?? "",
                color: this.quickView.color,
                rothco_item_no: this.quickView.rothco_item_no,
                item_index: product?.item_index ?? this.quickView.item_index,
                product: product ?? {
                    seo_name: "",
                    images: [],
                    item_variations: [],
                    item_specifications: [],
                },
                prices: prices ?? [],
                disable_quantity_input: this.quickView.disable_quantity_input ?? false,
                item_list_info: this.quickView.item_list_info ?? {list_id: null, list_item_id: null, quantity: 1},
                coupon_info: this.quickView.coupon_info ?? {},
                open_details_to_new_tab: this.quickView.open_details_to_new_tab ?? false
            }
        },
        clearQuickViewProduct(){
            this.quickView = {
                product_seo_name: "",
                color: "",
                rothco_item_no: "",
                item_index: null,
                product: {
                    seo_name: "",
                    images: [],
                    item_variations: [],
                    item_specifications: [],
                },
                prices: [],
                disable_quantity_input: false,
                item_list_info: {list_id: null, list_item_id: null, quantity: 1},
                coupon_info: {},
                open_details_to_new_tab: false
            }
        },
        setNotificationMessage(message) {
            this.notificationMessage = message;
            if(message == null && this.notificationTimeout){
                clearTimeout(this.notificationTimeout);
            }else{
                this.notificationTimeout = setTimeout(() => {
                    this.notificationMessage = null;
                }, 5000);
            }
           
        },
    }
});
// export const useSettingsStore = defineStore('settings', () => {
//     function addLink(link: Link) {
//         links.value.push(link);
//     }

//     function removeLink(link:Link) {
//         const index = links.value.findIndex(l => l.url == link.url);
//         links.value.splice(index, 1);
//     }
//     function sortLinks(a: Link, b: Link) {
//         if (a.sort && b.sort) {
//                 return a.sort - b.sort;
//               }
//               if (a.sort) {
//                 return -1;
//               }
//               if (b.sort) {
//                 return 1;
//               }
//               return 0;
//     }

//     const headerLinks = computed(() => links.value.filter(l => l.position == 'header').sort(sortLinks));
//     const footerLinks = computed(() => links.value.filter(l => l.position == 'footer').sort(sortLinks));

//     return {
//         links,
//         addLink,
//         removeLink,
//         headerLinks,
//         footerLinks
//     }
// })
